// Booking screen, Uber-style (like OtherUserProfile.swift on iOS): a route
// header, who's driving (or asking), the details, a seat stepper and one
// clear button. A right-hand drawer on desktop, a bottom sheet on phones.
// Contact details only appear after the booking is saved.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getDatabase, ref, get } from 'firebase/database';
import { bookRide, smsLink } from '../contacts';
import { displayPlace } from '../places';
import { SponsorLogo, sponsorAccentStyle, sponsorMarkURL, sponsorRideLabel, useRatingSummary, useSponsor, useSponsorTheme } from '../liveData';
import { firstNameOf, initialsOf } from '../userNames';
import { SafetyMenu } from '../Safety/Safety';
import AvatarPhoto from '../AvatarPhoto';
import './BookingSheet.css';

const MT = 'America/Denver';
const longDay = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'long', month: 'long', day: 'numeric' });
const clock   = new Intl.DateTimeFormat('en-US', { timeZone: MT, hour: 'numeric', minute: '2-digit' });

/** Driver's car from users/{uid}/car ({ make, model, color }), if they added one. */
function useCar(userID) {
  const [car, setCar] = useState(null);
  useEffect(() => {
    setCar(null);
    if (!userID) return;
    let live = true;
    get(ref(getDatabase(), `users/${userID}/car`)).then(s => { if (live) setCar(s.val()); }).catch(() => {});
    return () => { live = false; };
  }, [userID]);
  return car;
}

/** Two pins joined by a dashed arc: a stand-in map, since posts have no coordinates. */
function RouteArt() {
  return (
    <svg className="bsheet-art" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">
      <path d="M40 96 C 130 30, 230 26, 320 80" fill="none" stroke="rgba(255,255,255,.75)" strokeWidth="3" strokeDasharray="2 9" strokeLinecap="round" />
      <circle cx="40" cy="96" r="9" fill="#fff" /><circle cx="40" cy="96" r="4" fill="currentColor" />
      <path d="M320 58 a14 14 0 0 1 14 14 c0 10 -14 24 -14 24 s-14 -14 -14 -24 a14 14 0 0 1 14 -14z" fill="#fff" />
      <circle cx="320" cy="72" r="5" fill="currentColor" />
    </svg>
  );
}

/**
 * item: the ride or request (with _dep). kind: 'ride' | 'request'.
 * name: the poster's name. topDriver: { rides, riders } or undefined.
 */
export default function BookingSheet({ item, kind, name, topDriver, onClose, onBooked }) {
  const rating = useRatingSummary(item.userID);
  const isRide = kind === 'ride';
  const open = Math.max(0, parseInt(item.seats, 10) || 0);
  const [seats,  setSeats]  = useState(1);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [done,   setDone]   = useState(null); // contact after a successful booking
  const car = useCar(isRide ? item.userID : null);
  // Sponsored free rides get the sponsor's theme, like the app.
  const sponsorName = isRide ? item.sponsor : null;
  const sponsor = useSponsor();
  const theme = useSponsorTheme();
  const markURL = sponsorMarkURL(theme, sponsorName);
  const [copied, setCopied] = useState(false);
  const copyCode = () => {
    navigator.clipboard?.writeText(sponsor.code).then(() => setCopied(true)).catch(() => {});
  };
  const closeRef = useRef(null);
  const first = firstNameOf(name) || (isRide ? 'the driver' : 'the rider');

  useEffect(() => {
    closeRef.current?.focus();
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    document.body.classList.add('bsheet-lock');
    return () => { document.removeEventListener('keydown', esc); document.body.classList.remove('bsheet-lock'); };
  }, [onClose]);

  const confirm = async () => {
    setSaving(true); setError('');
    try {
      const contact = await bookRide({
        node: isRide ? 'rideReserve' : 'rideRequest', ride: item, seats, type: isRide ? 'reservation' : 'request',
      });
      setDone(contact);
      onBooked?.();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const carText = car && [car.color, car.make, car.model].filter(Boolean).join(' ');
  const donation = item.donationRequested ? String(item.donationRequested).replace(/^\$/, '').trim() : '';

  return ReactDOM.createPortal(
    <div className="bsheet-overlay" onClick={onClose}>
      <div className={`bsheet ${isRide ? 'ride' : 'request'}${sponsorName ? ' sponsored' : ''}`} style={sponsorAccentStyle(theme, sponsorName)} role="dialog" aria-modal="true"
        aria-label={isRide ? 'Book this ride' : 'Accept this request'} onClick={e => e.stopPropagation()}>

        <div className="bsheet-hero">
          <RouteArt />
          {markURL && (
            <span className="bsheet-sponsor-pin" aria-hidden="true">
              <img src={markURL} alt="" />
            </span>
          )}
          <button ref={closeRef} className="bsheet-close" onClick={onClose} aria-label="Close">✕</button>
          <div className="bsheet-hero-text">
            <span className="bsheet-kicker">{item.sponsor ? sponsorRideLabel(theme, item.sponsor) : isRide ? 'Ride' : 'Ride request'}</span>
            <h2>{displayPlace(item.fromLocation)} <span aria-hidden="true">→</span> {displayPlace(item.toLocation)}</h2>
            <p>{longDay.format(item._dep)} · {clock.format(item._dep)}</p>
          </div>
        </div>

        {done ? (
          sponsorName ? (
          <div className="bsheet-body bsheet-done bsheet-thanks">
            {markURL && <img className="bsheet-thanks-mark" src={markURL} alt="" />}
            <h3>You're booked!</h3>
            <p>Your free ride is on {sponsorName}.</p>
            {sponsor?.code && (
              <div className="bsheet-code-box">
                <strong>{sponsor.headline || 'Say thanks with a discount'}</strong>
                <button className="bsheet-code" onClick={copyCode} aria-label={`Copy code ${sponsor.code}`}>
                  {sponsor.code} <span aria-hidden="true">{copied ? '✓' : '⧉'}</span>
                </button>
                {sponsor.url && (
                  <a href={sponsor.url} target="_blank" rel="noopener noreferrer sponsored">
                    Shop {sponsor.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </div>
            )}
            {done.number && (
              <a className="bsheet-cta" href={smsLink(done.number)}>Text {first} · {done.number}</a>
            )}
            <button className="bsheet-secondary" onClick={onClose}>Done</button>
          </div>
          ) : (
          <div className="bsheet-body bsheet-done">
            <div className="bsheet-check" aria-hidden="true">✓</div>
            <h3>{isRide ? "You're booked!" : 'Request accepted!'}</h3>
            <p>
              {isRide
                ? `${seats} seat${seats !== 1 ? 's' : ''} with ${first}. Text them to sort out pickup.`
                : `You're driving ${first}. Text them to sort out pickup.`}
            </p>
            {done.number && (
              <a className="bsheet-cta" href={smsLink(done.number)}>Text {first} · {done.number}</a>
            )}
            <button className="bsheet-secondary" onClick={onClose}>Done</button>
          </div>
          )
        ) : (
          <div className="bsheet-body">
            {item.sponsor && <div className="bsheet-sponsor"><SponsorLogo name={item.sponsor} height={22} /></div>}
            <div className="bsheet-person">
              <span className="bsheet-avatar">{initialsOf(name)}<AvatarPhoto uid={item.userID} /></span>
              <span className="bsheet-person-main">
                <span className="bsheet-person-name">
                  {name || 'Falcon Ride user'}
                  {isRide && topDriver && <span className="bsheet-top">★ Top driver</span>}
                </span>
                <span className="bsheet-person-sub">
                  {isRide
                    ? (topDriver ? `${topDriver.rides} rides driven this year` : 'Driver')
                    : `Needs ${open} seat${open !== 1 ? 's' : ''}`}
                </span>
                {isRide && rating?.total >= 3 && (
                  <span className="bsheet-person-sub">
                    ★ {(rating.sum / rating.total).toFixed(1)} · {rating.total} ratings
                  </span>
                )}
              </span>
              <SafetyMenu target={{ userID: item.userID, name, contextType: isRide ? 'ride' : 'request', contextId: item.id }}
                onBlocked={onClose} />
            </div>

            <dl className="bsheet-facts">
              {isRide && <div><dt>Seats left</dt><dd>{open}</dd></div>}
              {carText && <div><dt>Car</dt><dd>{carText}</dd></div>}
              <div><dt>{isRide ? 'Contribution' : 'Offering'}</dt><dd>{item.sponsor ? <span className="bsheet-free">FREE</span> : donation ? `$${donation}` : 'None listed'}</dd></div>
            </dl>
            {item.additionalInfo && <p className="bsheet-note">“{item.additionalInfo}”</p>}

            {open > 0 ? (
              <>
                {/* Sponsored free rides are one seat per person. */}
                {!item.sponsor && <div className="bsheet-stepper">
                  <span>{isRide ? 'Seats to book' : 'Seats you can offer'}</span>
                  <div role="group" aria-label="Seats">
                    <button onClick={() => setSeats(n => Math.max(1, n - 1))} disabled={seats <= 1} aria-label="One fewer seat">−</button>
                    <output aria-live="polite">{seats}</output>
                    <button onClick={() => setSeats(n => Math.min(open, n + 1))} disabled={seats >= open} aria-label="One more seat">+</button>
                  </div>
                </div>}
                {error && <p className="bsheet-error" role="alert">{error}</p>}
                <button className="bsheet-cta" onClick={confirm} disabled={saving}>
                  {saving ? 'Saving…' : sponsorName ? 'Book free seat' : isRide ? `Confirm booking · ${seats} seat${seats !== 1 ? 's' : ''}` : `Accept request · ${seats} seat${seats !== 1 ? 's' : ''}`}
                </button>
                <p className="bsheet-fine">
                  {isRide ? `${first} gets a notification, and you'll see their number to text about pickup.`
                    : `${first} gets a notification, and you'll see their number to text about pickup.`}
                </p>
              </>
            ) : (
              <p className="bsheet-full">{isRide ? 'This ride is full.' : 'This request already has a driver.'}</p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
